import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  HelpCircle,
  Flag,
  Layers,
  FileText,
  BarChart3,
  PlayCircle,
  BookOpen,
  Lock,
  ChevronRight,
  Check,
  Trophy,
  RefreshCw,
  MonitorPlay,
  ChevronDown,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import Nav from "../Components/Nav/Nav";
import Menu from "../Components/Nav/Menu";
import Login from "./Login";
import { UidContext } from "../Components/App.Context";
import { getLearnFormation, enrollFormation } from "../actions/formationLearner.actions";

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => a.order - b.order);
const COVER_RING_C = 2 * Math.PI * 52;

const FormationDetail = ({ socket }) => {
  const { id } = useParams();
  const uid = useContext(UidContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current } = useSelector((s) => s.formationLearnerReducer);
  const [showSupport, setShowSupport] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    dispatch(getLearnFormation(id)).catch(() => {
      if (active) setLoadError(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch, id]);

  if (!uid) return <Login />;

  if (!current || !current.formation || String(current.formation.id) !== String(id)) {
    if (loadError) {
      return (
        <>
          <Nav socket={socket} />
          <div className="mainContainer">
            <Menu />
            <div className="center-Block">
              <div className="formations-panel">
                <div className="quiz-locked-notice">
                  <AlertTriangle size={40} />
                  <p>Formation indisponible. Elle a peut-être été dépubliée ou supprimée.</p>
                  <Link to="/formations" className="greenButton">
                    <ArrowLeft size={15} /> Retour aux formations
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <Nav socket={socket} />
        <div className="mainContainer">
          <Menu />
          <div className="center-Block">
            <p>Chargement…</p>
          </div>
        </div>
      </>
    );
  }

  const { formation, progress } = current;
  const modules = sortByOrder(formation.FormationModules);
  const quizzes = formation.FormationQuizzes || [];
  const finalQuiz = quizzes.find((q) => !q.moduleId);
  const moduleQuiz = (mid) => quizzes.find((q) => q.moduleId === mid);
  const completedSet = new Set(progress.completedLessonIds || []);
  const orderedLessons = modules.flatMap((m) => sortByOrder(m.FormationLessons));
  // Un module sans leçon est ignoré dans le déverrouillage (complet par convention),
  // même règle que le backend : il ne bloque jamais le quizz final.
  const isModuleComplete = (m) => {
    const ls = m.FormationLessons || [];
    return ls.length === 0 || ls.every((l) => completedSet.has(l.id));
  };
  const allModulesComplete = modules.length > 0 && modules.every(isModuleComplete);
  const firstUncompleted = orderedLessons.find((l) => !completedSet.has(l.id));
  const resumeLessonId =
    (firstUncompleted && firstUncompleted.id) ||
    progress.lastLessonId ||
    (orderedLessons[0] && orderedLessons[0].id);
  const cert = progress.certificate;

  const lessonCount = orderedLessons.length;
  const quizCount = quizzes.length;
  const percent = lessonCount ? Math.round((completedSet.size / lessonCount) * 100) : 0;
  const started = !!progress.enrollment;

  const start = async () => {
    try {
      await dispatch(enrollFormation(formation.id));
      if (resumeLessonId) navigate(`/formations/${formation.id}/apprendre/${resumeLessonId}`);
    } catch (e) {
      toast.error("Action impossible");
    }
  };

  return (
    <>
      <ToastContainer />
      <Nav socket={socket} />
      <div className="mainContainer">
        <Menu />
        <div className="center-Block">
          <div className="formations-panel course-landing">
            {/* Hero / page de garde */}
            <div className="course-hero">
              <div className="course-hero-cover">
                {formation.FormationCategory ? (
                  <span className="formation-cat">{formation.FormationCategory.name}</span>
                ) : null}

                <div className="course-cover-ring" title={`${percent}% complété`}>
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle className="cover-ring-track" cx="60" cy="60" r="52" />
                    <circle
                      className="cover-ring-bar"
                      cx="60"
                      cy="60"
                      r="52"
                      style={{
                        strokeDasharray: COVER_RING_C,
                        strokeDashoffset: COVER_RING_C * (1 - percent / 100),
                      }}
                    />
                  </svg>
                  <div className="course-cover-ring-label">
                    <strong>{percent}%</strong>
                    <span>complété</span>
                  </div>
                </div>

                <div className="course-cover-foot">
                  <BookOpen size={15} />
                  <span>
                    {lessonCount} leçon{lessonCount > 1 ? "s" : ""}
                    {formation.level ? ` · ${formation.level}` : ""}
                  </span>
                </div>
              </div>

              <div className="course-hero-body">
                <h1>{formation.title}</h1>
                {formation.description ? (
                  <p className="course-hero-desc">{formation.description}</p>
                ) : null}

                <div className="course-meta">
                  <span>
                    <Layers size={15} /> {modules.length} module{modules.length > 1 ? "s" : ""}
                  </span>
                  <span>
                    <FileText size={15} /> {lessonCount} leçon{lessonCount > 1 ? "s" : ""}
                  </span>
                  <span>
                    <HelpCircle size={15} /> {quizCount} quizz
                  </span>
                  {formation.level ? (
                    <span>
                      <BarChart3 size={15} /> {formation.level}
                    </span>
                  ) : null}
                </div>

                {started ? (
                  <div className="course-progress">
                    <div className="course-progress-bar">
                      <span style={{ width: `${percent}%` }} />
                    </div>
                    <span className="course-progress-label">{percent}% complété</span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Récompense / gamification — un badge par formation */}
            {(() => {
              const earned = cert && cert.status === "valid";
              const suspended = cert && cert.status === "suspended";
              const lessonsLeft = Math.max(0, orderedLessons.length - completedSet.size);
              const earnedDate =
                earned && cert.issuedAt
                  ? new Date(cert.issuedAt).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : null;
              return (
                <div className={`course-reward ${earned ? "earned" : suspended ? "suspended" : ""}`}>
                  <div className="reward-medal">
                    <Trophy size={30} />
                    {!earned && !suspended ? (
                      <span className="reward-lock">
                        <Lock size={13} />
                      </span>
                    ) : null}
                  </div>
                  <div className="reward-info">
                    <p className="reward-eyebrow">
                      {earned ? "Badge obtenu" : suspended ? "Badge suspendu" : "Récompense à débloquer"}
                    </p>
                    <h3>Badge « {formation.title} »</h3>
                    <p className="reward-sub">
                      {earned
                        ? earnedDate
                          ? `Félicitations, formation validée le ${earnedDate}.`
                          : "Félicitations, formation validée !"
                        : suspended
                        ? "La formation a été mise à jour : repassez-la pour récupérer votre badge."
                        : lessonsLeft > 0
                        ? `Plus que ${lessonsLeft} leçon${lessonsLeft > 1 ? "s" : ""} et le quizz final pour le décrocher !`
                        : "Passez le quizz final pour décrocher votre badge !"}
                    </p>
                    {earned ? (
                      <p className="reward-note">
                        <RefreshCw size={13} />
                        <span>
                          Si la formation est mise à jour, elle pourra être rouverte : il faudra alors la repasser
                          pour conserver votre badge.
                        </span>
                      </p>
                    ) : null}
                    {!earned ? (
                      <div className="reward-progress">
                        <div className="reward-progress-bar">
                          <span style={{ width: `${percent}%` }} />
                        </div>
                        <span>{percent}%</span>
                      </div>
                    ) : null}
                  </div>
                  {earned ? (
                    <span className="reward-status">
                      <Check size={15} /> Validé
                    </span>
                  ) : (
                    <button className="greenButton reward-cta" onClick={start} disabled={!resumeLessonId}>
                      <PlayCircle size={16} /> {started ? "Continuer" : "Commencer"}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Support d'origine (Genially ou vidéo Loom) — consultable, replié par défaut */}
            {formation.sourceEmbedUrl ? (
              <div className={`course-support ${showSupport ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="course-support-head"
                  onClick={() => setShowSupport((v) => !v)}
                  aria-expanded={showSupport}
                >
                  <span className="course-support-title">
                    <MonitorPlay size={18} />
                    Support de la formation
                  </span>
                  <span className="course-support-toggle">
                    {showSupport ? "Replier" : "Déplier"}
                    <ChevronDown size={16} />
                  </span>
                </button>
                {showSupport ? (
                  <div className="course-support-frame">
                    <iframe
                      title={`Support — ${formation.title}`}
                      src={formation.sourceEmbedUrl}
                      frameBorder="0"
                      allowFullScreen
                      allow="fullscreen"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Programme détaillé */}
            <div className="course-section">
              <h2>Programme</h2>
              <div className="formation-curriculum">
                {modules.map((m, mi) => {
                  const mLessons = sortByOrder(m.FormationLessons);
                  const doneCount = mLessons.filter((l) => completedSet.has(l.id)).length;
                  const mComplete = isModuleComplete(m);
                  const mPct = mLessons.length ? Math.round((doneCount / mLessons.length) * 100) : 0;
                  return (
                  <div className={`curriculum-module ${mComplete ? "is-complete" : ""}`} key={m.id}>
                    <div className="module-card-head">
                      <span className={`module-num ${mComplete ? "done" : ""}`}>
                        {mComplete ? <Check size={16} /> : mi + 1}
                      </span>
                      <div className="module-head-text">
                        <h3>{m.title}</h3>
                        <div className="module-mini-progress">
                          <div className="bar">
                            <span style={{ width: `${mPct}%` }} />
                          </div>
                          <span className="module-count">
                            {doneCount}/{mLessons.length} leçon{mLessons.length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    {mLessons.map((l) => (
                      <Link
                        key={l.id}
                        to={`/formations/${formation.id}/apprendre/${l.id}`}
                        className="curriculum-lesson"
                      >
                        {completedSet.has(l.id) ? (
                          <CheckCircle2 size={17} className="done" />
                        ) : (
                          <PlayCircle size={17} className="lead-play" />
                        )}
                        <span className="grow">{l.title}</span>
                        <ChevronRight size={16} className="row-chevron" />
                      </Link>
                    ))}
                    {moduleQuiz(m.id) ? (
                      isModuleComplete(m) ? (
                        <Link
                          to={`/formations/${formation.id}/quizz/${moduleQuiz(m.id).id}`}
                          className="curriculum-quiz"
                        >
                          <HelpCircle size={16} /> Quizz du module
                        </Link>
                      ) : (
                        <span
                          className="curriculum-quiz locked"
                          title="Terminez les leçons du module pour débloquer le quizz"
                        >
                          <Lock size={16} /> Quizz du module
                        </span>
                      )
                    ) : null}
                  </div>
                  );
                })}
                {finalQuiz ? (
                  allModulesComplete ? (
                    <Link
                      to={`/formations/${formation.id}/quizz/${finalQuiz.id}`}
                      className="curriculum-quiz final"
                    >
                      <Flag size={16} /> Quizz final
                    </Link>
                  ) : (
                    <span
                      className="curriculum-quiz final locked"
                      title="Terminez tous les modules pour débloquer le quizz final"
                    >
                      <Lock size={16} /> Quizz final
                    </span>
                  )
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormationDetail;
